/**
 * Magic Link Page Component
 *
 * Magic link sign-in page with email input for requesting sign-in link.
 * Uses the platform-appropriate shared authentication layout.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { AuthRouteLayout } from '@/components/auth/auth-route-layout';
import { WEB_BACK_LINK_CLASS } from '@/components/auth/auth-page-chrome';
import {
  AUTH_BUTTON_CLASS,
  AUTH_DESCRIPTION_CLASS,
  AUTH_HEADING_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_LINK_CLASS,
} from '@/components/auth/auth-styles';
import { AuthTextField } from '@/components/auth/auth-text-field';
import { FormField, HookForm } from '@/components/base/form/hook-form';
import { entryBackLinkClass } from '@/components/brand/entry-styles';
import { Button } from '@/components/base/buttons/button';
import { useMagicLinkSignIn } from '@/hooks/use-magic-link-signin';
import { isDesktopPlatform } from '@/lib/desktop/bridge';

function createFormSchema(invalidEmail: string) {
  return z.object({
    email: z.string().email({ message: invalidEmail }),
  });
}

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

const MagicLink = () => {
  const { t } = useTranslation('auth');
  const { isLoading, sendMagicLink } = useMagicLinkSignIn();
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const desktop = isDesktopPlatform();

  const formSchema = useMemo(() => createFormSchema(t('login.invalidEmail')), [t]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleSubmit = async (values: FormData) => {
    await sendMagicLink(values.email);
    setEmailSent(true);
  };

  return (
    <AuthRouteLayout>
      {/* Back to Login. On the desktop entry canvas the link is seated on the
          content column's left edge; see `entryBackLinkClass`. */}
      <Button
        className={desktop ? entryBackLinkClass : WEB_BACK_LINK_CLASS}
        color="tertiary"
        size="md"
        onClick={() => {
          Promise.resolve(navigate('/auth/login')).catch(() => null);
        }}
      >
        <ArrowLeft className="mr-2 size-4" />
        {t('magicLink.back')}
      </Button>

      {emailSent ? (
        <div className="flex flex-col gap-4">
          <div className="mb-2">
            <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-landing-fg/5">
              <svg
                className="size-6 text-landing-fg/60"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className={AUTH_HEADING_CLASS}>{t('magicLink.sentHeading')}</h1>
            <p className={AUTH_DESCRIPTION_CLASS}>
              {t('magicLink.sentDescription')}{' '}
              <span className="font-medium text-landing-fg">{form.getValues('email')}</span>
            </p>
            <p className="mt-1 text-xs text-landing-fg/30">{t('magicLink.sentHint')}</p>
          </div>

          <Button
            className={AUTH_BUTTON_CLASS}
            size="md"
            type="button"
            onClick={() => setEmailSent(false)}
          >
            {t('magicLink.sendAgain')}
          </Button>
        </div>
      ) : (
        <HookForm
          className="flex flex-col gap-4"
          form={form}
          onSubmit={(e) => {
            form
              .handleSubmit(handleSubmit)(e)
              .catch(() => {
                /* handled by react-hook-form */
              });
          }}
        >
          <div className="mb-2">
            <h1 className={AUTH_HEADING_CLASS}>{t('magicLink.heading')}</h1>
            <p className={AUTH_DESCRIPTION_CLASS}>{t('magicLink.description')}</p>
          </div>

          <FormField control={form.control} name="email">
            {({ field, fieldState }) => (
              <AuthTextField
                {...field}
                autoComplete="email"
                hint={fieldState.error?.message}
                inputClassName={AUTH_INPUT_CLASS}
                isInvalid={fieldState.invalid}
                label={t('login.emailLabel')}
                labelClassName={AUTH_LABEL_CLASS}
                placeholder={t('login.emailPlaceholder')}
                type="email"
              />
            )}
          </FormField>

          <Button className={AUTH_BUTTON_CLASS} isDisabled={isLoading} size="md" type="submit">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                {t('magicLink.sending')}
              </>
            ) : (
              t('magicLink.sendLink')
            )}
          </Button>
        </HookForm>
      )}

      <div className="mt-6 text-center">
        <span className="text-xs text-landing-fg/30">{t('signInPrompt.remember')} </span>
        <Button
          className={AUTH_LINK_CLASS}
          color="link-color"
          size="md"
          onClick={() => {
            Promise.resolve(navigate('/auth/login')).catch(() => null);
          }}
        >
          {t('signInPrompt.signIn')}
        </Button>
      </div>
    </AuthRouteLayout>
  );
};

export default MagicLink;
